import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Zap, FileText, User, Calendar, Check, Eraser, Loader2,
  Building, Mail, Phone, MapPin, Camera, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatCurrency, formatDate, API } from '../lib/utils';

const PublicSignaturePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const fetchQuoteData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sign/${token}`);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching quote:', err);
      setError(err.response?.data?.detail || 'Lien invalide ou expiré');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchQuoteData();
  }, [fetchQuoteData]);

  useEffect(() => {
    if (canvasRef.current && !loading && !error) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [loading, error]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSubmit = async () => {
    if (!hasDrawn) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }

    setSubmitting(true);
    try {
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      const base64 = dataUrl.split(',')[1];

      const response = await axios.post(`${API}/sign/${token}`, {
        signature_image_base64: base64
      });

      if (response.status === 200) {
        setSigned(true);
        toast.success('Devis signé avec succès !');
      } else {
        throw new Error('Réponse inattendue');
      }
    } catch (err) {
      console.error('Signature error:', err);
      toast.error(err.response?.data?.detail || 'Erreur lors de la signature');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement du devis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Lien invalide</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-bold mb-2">Devis signé !</h2>
            <p className="text-muted-foreground mb-6">
              Merci ! Votre signature a été enregistrée. L'artisan a été notifié.
            </p>
            <p className="text-sm text-muted-foreground">
              Vous pouvez fermer cette page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { quote, client, company } = data;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold">DevisFast</h1>
            <p className="text-xs text-muted-foreground">Signature de devis</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Company Info */}
        {company && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {company.logo ? (
                  <img 
                    src={company.logo} 
                    alt={company.name}
                    className="w-16 h-16 object-contain rounded"
                  />
                ) : (
                  <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                    <Building className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="font-bold text-lg">{company.name}</h2>
                  {company.forme_juridique && (
                    <p className="text-sm text-muted-foreground">{company.forme_juridique}</p>
                  )}
                  {company.address && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {company.address}, {company.postal_code} {company.city}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                    {company.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {company.phone}
                      </span>
                    )}
                    {company.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {company.email}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quote Info */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Devis {quote.quote_number}
              </CardTitle>
              <Badge variant="outline">{quote.status}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Client */}
            {client && (
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-muted-foreground" />
                <span>Pour : <strong>{client.name}</strong></span>
              </div>
            )}
            
            {/* Date */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>Créé le {quote.created_at ? formatDate(quote.created_at) : ''}</span>
<span>• Valide jusqu'au {quote.validity_date ? formatDate(quote.validity_date.substring(0,10)) : (quote.created_at && quote.validity_days ? formatDate(new Date(new Date(quote.created_at).getTime() + quote.validity_days * 86400000).toISOString().substring(0,10)) : '')}</span>
            </div>

            {/* Lines */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3">Description</th>
                    <th className="text-right p-3">Montant HT</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.lines?.map((line, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="p-3">
                        <p className="font-medium">{line.description}</p>
                        <p className="text-muted-foreground text-xs">
  {line.quantity} × {formatCurrency(line.unit_price_ht)} · TVA {line.tva_rate}%
</p>
                      </td>
                      <td className="p-3 text-right">
                        {formatCurrency(line.total_ht)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="bg-muted/30 rounded-lg p-4 space-y-2">
              {(() => {
                const subtotalHT = quote.lines?.reduce((s, l) => s + (l.total_ht || 0), 0) || 0;
                const discountAmt = subtotalHT - quote.total_ht;
                return (
                  <>
                    {discountAmt > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Sous-total HT</span>
                        <span>{formatCurrency(subtotalHT)}</span>
                      </div>
                    )}
                    {discountAmt > 0 && (
                      <div className="flex justify-between text-sm text-red-500">
                        <span>Remise ({quote.discount_type === 'percent' ? `${quote.discount_value}%` : 'fixe'})</span>
                        <span>-{formatCurrency(discountAmt)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span>{discountAmt > 0 ? 'Net HT' : 'Total HT'}</span>
                      <span>{formatCurrency(quote.total_ht)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>TVA</span>
                      <span>{formatCurrency(quote.total_tva)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t border-border">
                      <span>Total TTC</span>
                      <span className="text-primary">{formatCurrency(quote.total_ttc)}</span>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Notes */}
            {quote.notes && (
              <div className="text-sm">
                <p className="font-medium mb-1">Notes</p>
                <p className="text-muted-foreground whitespace-pre-wrap">{quote.notes}</p>
              </div>
            )}

            {/* Photos */}
            {quote.photos && quote.photos.length > 0 && (
              <div>
                <p className="font-medium mb-2 flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Photos chantier
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {quote.photos.map((photo, i) => (
                    <img
                      key={i}
                      src={`data:image/jpeg;base64,${photo.data_base64}`}
                      alt={photo.filename || `Photo ${i + 1}`}
                      className="aspect-square object-cover rounded-lg"
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="text-center">Votre signature</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-sm text-muted-foreground">
              Signez ci-dessous pour accepter ce devis.
            </p>

            {/* Canvas */}
            <div className="border-2 border-dashed border-border rounded-lg overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                width={400}
                height={200}
                className="w-full touch-none cursor-crosshair"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                data-testid="public-signature-canvas"
              />
            </div>

            {/* Clear */}
            <Button
              type="button"
              variant="outline"
              onClick={clearCanvas}
              className="w-full"
              data-testid="clear-public-signature-btn"
            >
              <Eraser className="w-4 h-4 mr-2" />
              Effacer
            </Button>

            {/* Legal text */}
            <p className="text-xs text-muted-foreground text-center">
              En signant, j'accepte ce devis et ses conditions.
              Ma signature sera horodatée et mon adresse IP enregistrée.
            </p>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={!hasDrawn || submitting}
              className="w-full bg-green-600 hover:bg-green-700"
              size="lg"
              data-testid="submit-public-signature-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signature en cours...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Signer ce devis
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground pb-8">
          Propulsé par DevisFast · Devis & factures pour artisans
        </p>
      </div>
    </div>
  );
};

export default PublicSignaturePage;
